/**
 * useTransactionStore — Global per-node transaction store.
 *
 * Architecture:
 *   This store holds ALL known transactions for every node in the session.
 *   It is the single source of truth for Node Inspector (Transfers + Counterparties tabs).
 *   Canvas elements[] is the DISPLAY layer — a subset of transactions promoted to canvas.
 *
 *   Data survives canvas changes (eye toggle, recompute, filters don't affect this store).
 *   Every code path that fetches transactions writes here via setNodeTransactions/appendNodeTransactions.
 *   Node Inspector reads from here via getNodeTransactions(selectedNode.id).
 *
 * Relationship to canvas:
 *   - Node Inspector reads: useTransactionStore.getNodeTransactions(nodeId)
 *   - Canvas reads: elements[] in useGraphStore (display layer)
 *   - Promotion: user or algorithm decides which txs go to canvas
 *   - Visibility: eye toggle / graph filters control _visibility on canvas elements
 *
 * See: docs/CANVAS_DATA_DISPLAY_SEPARATION.md
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { TransactionEvidence } from '../types/graph';
import { createQuotaSafeSessionStorage } from '../utils/quotaSafeStorage';

/** Coerce any value to string at the data boundary — prevents React #185 everywhere downstream. */
const _s = (v: any): string | null => {
    if (v == null) return null;
    if (typeof v === 'string') return v;
    if (typeof v === 'object') return v.name || v.entity_name || v.label || v.symbol || v.value || null;
    return String(v);
};

/** Convert a raw API transaction to the standard TransactionEvidence format. */
export function toExpandedTx(tx: any, nodeAddress: string): TransactionEvidence {
    const nodeAddrLower = nodeAddress.toLowerCase();
    const fromLower = (tx.from_address || '').toLowerCase();
    const isSender = fromLower === nodeAddrLower;

    // Spread ALL extra fields first (UTXO, privacy chain, extended forensic data).
    // Then override with explicitly-typed core fields so display logic stays stable.
    // TransactionEvidence extends Extensible (Record<string,any>) so extra keys are safe.
    const extra: Record<string, any> = {};
    const CORE_KEYS = new Set([
        'tx_hash', 'hash', 'from_address', 'to_address', 'value', 'fee',
        'timestamp', 'block_height', 'token_symbol', 'historical_usd',
        'direction', 'counterparty', 'counterparty_entity', 'counterparty_type',
        'counterparty_risk', 'other_address_entity_name', 'other_address_entity_type',
        'other_address_risk_score',
    ]);
    for (const key of Object.keys(tx)) {
        if (!CORE_KEYS.has(key) && tx[key] !== undefined && tx[key] !== null) {
            extra[key] = tx[key];
        }
    }

    return {
        // Preserved extra fields: all_inputs, all_outputs, utxo_inputs, ring_size,
        // mixing_depth, protocol_era, key_images, is_privacy_chain, is_coinjoin,
        // is_unshielding, exit_amount, data_source, nonce, etc.
        ...extra,
        // Core mapped fields (explicit typing for display logic)
        tx_hash: tx.tx_hash || tx.hash || '',
        from_address: tx.from_address,
        to_address: tx.to_address,
        value: typeof tx.value === 'number' ? tx.value : parseFloat(tx.value) || 0,
        fee: tx.fee,
        timestamp: tx.timestamp ?? null,
        block_height: tx.block_height,
        token_symbol: _s(tx.token_symbol),
        historical_usd: tx.historical_usd,
        direction: isSender ? 'outgoing' : 'incoming',
        counterparty: isSender ? tx.to_address : tx.from_address,
        // EVM _enrich_txs_with_entity_db writes from_entity_name/to_entity_name (directional),
        // Tron fetcher writes other_address_entity_name, Arkham writes both. Fall through all
        // three so counterparty_entity is populated regardless of which backend path ran.
        counterparty_entity: _s(tx.other_address_entity_name)
            || _s(isSender ? tx.to_entity_name : tx.from_entity_name)
            || _s(tx.counterparty_entity)
            || null,
        counterparty_type: _s(tx.other_address_entity_type)
            || _s(isSender ? tx.to_entity_type : tx.from_entity_type)
            || _s(tx.counterparty_type)
            || null,
        counterparty_risk: tx.other_address_risk_score ?? tx.counterparty_risk,
    };
}

/** Convert an array of raw API transactions for a given node address. */
export function toExpandedTxs(txs: any[], nodeAddress: string): TransactionEvidence[] {
    return txs.map(tx => toExpandedTx(tx, nodeAddress));
}

export interface TransactionStoreState {
    /** Per-node transaction map: address (lowercase) → transactions */
    nodeTransactions: Map<string, TransactionEvidence[]>;

    /** Set all transactions for a node (replaces existing). */
    setNodeTransactions: (nodeAddress: string, txs: TransactionEvidence[]) => void;

    /** Append transactions to a node (deduplicates by tx_hash). */
    appendNodeTransactions: (nodeAddress: string, txs: TransactionEvidence[]) => void;

    /** Get transactions for a node. Returns empty array if none. */
    getNodeTransactions: (nodeAddress: string) => TransactionEvidence[];

    /** Check if we have any transactions for a node. */
    hasTransactions: (nodeAddress: string) => boolean;

    /** Get transaction count for a node. */
    getTransactionCount: (nodeAddress: string) => number;

    /** Remove all transactions for a node. */
    clearNodeTransactions: (nodeAddress: string) => void;

    /** Clear everything (session reset). */
    clearAll: () => void;
}

const EMPTY_TXS: TransactionEvidence[] = [];

/**
 * Persist caps — sessionStorage is ~5 MB shared across workbench keys.
 * In-memory `nodeTransactions` is unbounded (append-only). Only the
 * snapshot written to sessionStorage is trimmed so a large investigation
 * cannot crash the page with QuotaExceededError on `workbench-transactions`.
 */
export const PERSIST_MAX_TXS_PER_NODE = 80;
export const PERSIST_MAX_NODES = 40;

const PERSIST_TX_KEYS = [
    'tx_hash', 'from_address', 'to_address', 'value', 'fee',
    'timestamp', 'block_height', 'token_symbol', 'historical_usd',
    'direction', 'counterparty', 'counterparty_entity', 'counterparty_type',
    'counterparty_risk', 'chain',
] as const;

function slimTxForPersist(tx: TransactionEvidence): TransactionEvidence {
    const slim: Record<string, unknown> = {};
    for (const key of PERSIST_TX_KEYS) {
        const val = (tx as Record<string, unknown>)[key];
        if (val !== undefined && val !== null) slim[key] = val;
    }
    return slim as TransactionEvidence;
}

/** Bound + slim a transaction map for sessionStorage persist. In-memory store is unchanged. */
export function capTransactionsForPersist(
    nodeTransactions: Map<string, TransactionEvidence[]>,
): Map<string, TransactionEvidence[]> {
    const entries = Array.from(nodeTransactions.entries());
    const kept = entries.length > PERSIST_MAX_NODES
        ? entries.slice(-PERSIST_MAX_NODES)
        : entries;
    const next = new Map<string, TransactionEvidence[]>();
    for (const [addr, txs] of kept) {
        const recent = txs.length > PERSIST_MAX_TXS_PER_NODE
            ? txs.slice(-PERSIST_MAX_TXS_PER_NODE)
            : txs;
        next.set(addr, recent.map(slimTxForPersist));
    }
    return next;
}

/** Sanitize a single tx's string-like fields that can be objects from API. */
const sanitizeTx = (tx: TransactionEvidence): TransactionEvidence => {
    let changed = false;
    const out = { ...tx };
    for (const key of ['token_symbol', 'counterparty_entity', 'counterparty_type', 'from_entity_name', 'to_entity_name'] as const) {
        const val = (out as any)[key];
        if (val && typeof val === 'object' && !Array.isArray(val)) {
            (out as any)[key] = val.name || val.entity_name || val.label || val.symbol || null;
            changed = true;
        }
    }
    return changed ? out : tx;
};
const sanitizeTxs = (txs: TransactionEvidence[]): TransactionEvidence[] => txs.map(sanitizeTx);

/** Custom sessionStorage adapter that handles Map serialization + quota. */
const mapStorage = createJSONStorage<{ nodeTransactions: Map<string, TransactionEvidence[]> }>(
    () => createQuotaSafeSessionStorage({
        warnKey: '__workbench_tx_quota_warned__',
        warnMessage:
            '[useTransactionStore] sessionStorage quota exceeded — transaction cache will not persist across reloads. ' +
            'Use Save Snapshot to keep this investigation.',
    }),
    {
        reviver: (_key, value) => {
            // Revive serialized Map: stored as { __map: [[key, value], ...] }
            if (value && typeof value === 'object' && '__map' in value && Array.isArray((value as any).__map)) {
                return new Map((value as any).__map);
            }
            return value;
        },
        replacer: (_key, value) => {
            // Serialize Map as { __map: [[key, value], ...] }
            if (value instanceof Map) {
                return { __map: Array.from(value.entries()) };
            }
            return value;
        },
    },
);

export const useTransactionStore = create<TransactionStoreState>()(
    persist(
    (set, get) => ({
    nodeTransactions: new Map(),

    setNodeTransactions: (nodeAddress: string, txs: TransactionEvidence[]) => {
        // setNodeTransactions is now an APPEND operation — Node Inspector
        // is an ever-expanding list. We never lose data. Deduplicates by tx_hash.
        // Use clearNodeTransactions() for explicit reset (e.g., session clear).
        set(state => {
            const key = nodeAddress.toLowerCase();
            const next = new Map(state.nodeTransactions);
            const existing = next.get(key) || [];
            const safeTxs = sanitizeTxs(txs);
            if (existing.length === 0) {
                next.set(key, safeTxs);
            } else if (safeTxs.length > 0) {
                const existingHashes = new Set(existing.map(t => t.tx_hash));
                const newTxs = safeTxs.filter(t => t.tx_hash && !existingHashes.has(t.tx_hash));
                if (newTxs.length > 0) {
                    next.set(key, [...existing, ...newTxs]);
                } else {
                    return state; // no new data, skip re-render
                }
            } else {
                return state; // empty input, keep existing
            }
            return { nodeTransactions: next };
        });
    },

    appendNodeTransactions: (nodeAddress: string, txs: TransactionEvidence[]) => {
        set(state => {
            const key = nodeAddress.toLowerCase();
            const existing = state.nodeTransactions.get(key) || [];
            const safeTxs = sanitizeTxs(txs);
            const existingHashes = new Set(existing.filter(t => t.tx_hash).map(t => t.tx_hash));
            // Allow tx if it has no hash (synthetic) OR if its hash isn't in the store yet
            const newTxs = safeTxs.filter(t => !t.tx_hash || !existingHashes.has(t.tx_hash));
            
            if (newTxs.length === 0) {
                return state; // No new data, skip update to prevent re-render loop
            }

            const next = new Map(state.nodeTransactions);
            next.set(key, [...existing, ...newTxs]);
            return { nodeTransactions: next };
        });
    },

    getNodeTransactions: (nodeAddress: string) => {
        return get().nodeTransactions.get(nodeAddress.toLowerCase()) || EMPTY_TXS;
    },

    hasTransactions: (nodeAddress: string) => {
        const txs = get().nodeTransactions.get(nodeAddress.toLowerCase());
        return !!txs && txs.length > 0;
    },

    getTransactionCount: (nodeAddress: string) => {
        return (get().nodeTransactions.get(nodeAddress.toLowerCase()) || []).length;
    },

    clearNodeTransactions: (nodeAddress: string) => {
        set(state => {
            const next = new Map(state.nodeTransactions);
            next.delete(nodeAddress.toLowerCase());
            return { nodeTransactions: next };
        });
    },

    clearAll: () => {
        set({ nodeTransactions: new Map() });
    },
}),
    {
        name: 'workbench-transactions',
        storage: mapStorage,
        version: 1,
        partialize: (state) => ({
            nodeTransactions: capTransactionsForPersist(state.nodeTransactions),
        }),
    }
));

// Dev-tool exposure — read-only handle for the Ctrl+Shift+E debug snapshot
// (canvasDebug.exportSession reads `window.__useTransactionStore` to dump the
// per-node tx store alongside the canvas state). Same pattern as
// `__useGraphStore`, `__useSelectionStore`, `__useFilterStore`.
if (typeof window !== 'undefined') {
    (window as any).__useTransactionStore = useTransactionStore;
}
